/**
 * Runtime/buildtime configuration for Workplace Visitors.
 *
 * Resolution order per setting:
 *   1. `.env` at build time: `import.meta.env?.NG_APP_*`, filled by `@ngx-env/builder`
 *      from a local `.env` (or the shell), the same mechanism and names as the
 *      employee app. `.env.example` lists every name. Each read below is a literal
 *      `import.meta.env?.NG_APP_*` because the builder replaces exactly that
 *      expression; a computed key would never be replaced. ALWAYS optional-chained:
 *      `import.meta.env` is undefined in the Angular esbuild bundle, so a name with
 *      no value stays a live read, and a bare access crashes at runtime.
 *   2. `window.__RDX_VISITORS_ENV__.<KEY>`: runtime injection through an inline
 *      `index.html` snippet, by deploys (see cloudbuild.yaml) and by
 *      `src/index.platform.html` for `npm start`.
 *   3. Hard defaults: `PLATFORM_MODE=false`, so demo behaviour is unchanged
 *      everywhere unless a build explicitly opts in, and no `.env` is needed for
 *      demo mode or `/preview`. Settings the app does not read yet default to ''.
 *
 * A document opened on `/preview` is a preview session (`preview/preview-session.ts`):
 * `PLATFORM_MODE` is forced off for its whole life, whatever the build injected.
 */

import { isPreviewSession } from '../preview/preview-session';

export interface RdxVisitorsEnv {
  /** true → real auth + rdx-platform-api data; false → demo mode (default). */
  PLATFORM_MODE: boolean;
  /** true when this document opened on `/preview`: demo data, no sign-in, no network. */
  PREVIEW_SESSION: boolean;
  /** Origin of the existing public questionnaire; required before invitation rollout. */
  QUESTIONNAIRE_APP_URL: string;
  /** GraphQL endpoint for platform mode. */
  API_URL: string;
  /** login-web origin for the auth redirect. */
  LOGIN_APP_URL: string;
  /**
   * App identifier sent to login-web (`?app=`). login-web returns the user only to
   * an app id on its allow-list. Default `rdx-visitors`.
   */
  APP_ID: string;
  /**
   * PostHog project API key (public, browser-shipped ingestion key). Empty
   * string = telemetry OFF (demo builds, or platform builds before a key is
   * wired). Never a server secret — see analytics.service.ts.
   */
  POSTHOG_KEY: string;
  /** PostHog ingestion host (EU cloud by default). */
  POSTHOG_HOST: string;
  /**
   * The other RICOH Spaces apps the nav's app chips open. Empty means the
   * chip is not shown. Local defaults on localhost; deployed builds inject them.
   */
  EMPLOYEE_APP_URL: string;
  MANAGE_APP_URL: string;
  INSIGHTS_APP_URL: string;
  SETTINGS_APP_URL: string;
  /**
   * Held for features Visitors does not have yet (maps, assets, Teams, web push),
   * so a `.env` written for the employee app works here unchanged. Nothing reads
   * them yet; each is '' unless configured.
   */
  MAPBOX_KEY: string;
  CDN_BASE_URL: string;
  TEAMS_CLIENT_ID: string;
  TEAMS_AUTHORITY: string;
  FIREBASE_API_KEY: string;
  FIREBASE_AUTH_DOMAIN: string;
  FIREBASE_PROJECT_ID: string;
  FIREBASE_STORAGE_BUCKET: string;
  FIREBASE_MESSAGING_SENDER_ID: string;
  FIREBASE_APP_ID: string;
  FIREBASE_VAPID_KEY: string;
}

/** Setting names, as the window snippet spells them. */
type SettingKey = keyof RdxVisitorsWindowEnv;

const DEFAULT_API_URL = 'https://dev-api.ricohworkplace.com/graphql';
const DEFAULT_LOGIN_APP_URL = 'https://dev-login.ricohworkplace.com';
const LOCAL_LOGIN_APP_URL = 'http://localhost:4003';
const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';
const DEFAULT_APP_ID = 'rdx-visitors';

/**
 * Step 1: the `.env` values, keyed by setting. Never throws; an unset name reads
 * as undefined.
 */
function buildTimeEnv(): Partial<Record<SettingKey, string>> {
  try {
    return {
      PLATFORM_MODE: import.meta.env?.NG_APP_PLATFORM_MODE,
      API_URL: import.meta.env?.NG_APP_SPACES_APP_API_URL,
      LOGIN_APP_URL: import.meta.env?.NG_APP_SPACES_APP_LOGIN_APP_URL,
      APP_ID: import.meta.env?.NG_APP_APP,
      POSTHOG_KEY: import.meta.env?.NG_APP_POSTHOG_PROJECT_TOKEN,
      POSTHOG_HOST: import.meta.env?.NG_APP_POSTHOG_HOST,
      QUESTIONNAIRE_APP_URL: import.meta.env?.NG_APP_QUESTIONNAIRE_APP_URL,
      EMPLOYEE_APP_URL: import.meta.env?.NG_APP_SPACES_APP_EMPLOYEE_APP_URL,
      MANAGE_APP_URL: import.meta.env?.NG_APP_MANAGE_URL,
      INSIGHTS_APP_URL: import.meta.env?.NG_APP_INSIGHTS_APP_URL,
      SETTINGS_APP_URL: import.meta.env?.NG_APP_SETTINGS_APP_URL,
      MAPBOX_KEY: import.meta.env?.NG_APP_SPACES_APP_MAPBOX_KEY,
      CDN_BASE_URL: import.meta.env?.NG_APP_SPACES_APP_CDN_BASE_URL,
      TEAMS_CLIENT_ID: import.meta.env?.NG_APP_TEAMS_CLIENT_ID,
      TEAMS_AUTHORITY: import.meta.env?.NG_APP_TEAMS_AUTHORITY,
      FIREBASE_API_KEY: import.meta.env?.NG_APP_FIREBASE_API_KEY,
      FIREBASE_AUTH_DOMAIN: import.meta.env?.NG_APP_FIREBASE_AUTH_DOMAIN,
      FIREBASE_PROJECT_ID: import.meta.env?.NG_APP_FIREBASE_PROJECT_ID,
      FIREBASE_STORAGE_BUCKET: import.meta.env?.NG_APP_FIREBASE_STORAGE_BUCKET,
      FIREBASE_MESSAGING_SENDER_ID: import.meta.env?.NG_APP_FIREBASE_MESSAGING_SENDER_ID,
      FIREBASE_APP_ID: import.meta.env?.NG_APP_FIREBASE_APP_ID,
      FIREBASE_VAPID_KEY: import.meta.env?.NG_APP_FIREBASE_VAPID_KEY,
    };
  } catch {
    return {};
  }
}

function windowEnv(): RdxVisitorsWindowEnv {
  if (typeof window === 'undefined') return {};
  return window.__RDX_VISITORS_ENV__ ?? {};
}

/**
 * First usable value wins: `.env` → window snippet. A blank `.env` line counts as
 * unset, so an empty `NG_APP_X=` never hides the snippet's value.
 */
function readSetting(key: SettingKey, buildTime = buildTimeEnv()): unknown {
  const fromBuild = buildTime[key];
  if (typeof fromBuild === 'string' && fromBuild.trim().length > 0) return fromBuild;
  const fromWindow = windowEnv()[key];
  if (fromWindow !== undefined) return fromWindow;
  return undefined;
}

function asBool(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === '0' || v === 'no' || v === '') return false;
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/** Resolve the env afresh — exported for tests; app code uses RDX_VISITORS_ENV. */
export function readRdxVisitorsEnv(): RdxVisitorsEnv {
  const preview = isPreviewSession();
  const buildTime = buildTimeEnv();
  const text = (key: SettingKey): string | undefined => asString(readSetting(key, buildTime));
  const plain = (key: SettingKey): string => text(key) ?? '';
  /** A sibling app's URL: configured, else its local port on localhost, else none. */
  const appUrl = (key: SettingKey, local: string): string =>
    text(key) ?? (isLocalhost() ? local : '');

  return {
    QUESTIONNAIRE_APP_URL: appUrl('QUESTIONNAIRE_APP_URL', 'http://localhost:4600'),
    PLATFORM_MODE: !preview && (asBool(readSetting('PLATFORM_MODE', buildTime)) ?? false),
    PREVIEW_SESSION: preview,
    API_URL: text('API_URL') ?? DEFAULT_API_URL,
    LOGIN_APP_URL:
      text('LOGIN_APP_URL') ?? (isLocalhost() ? LOCAL_LOGIN_APP_URL : DEFAULT_LOGIN_APP_URL),
    APP_ID: text('APP_ID') ?? DEFAULT_APP_ID,
    POSTHOG_KEY: plain('POSTHOG_KEY'),
    POSTHOG_HOST: text('POSTHOG_HOST') ?? DEFAULT_POSTHOG_HOST,
    EMPLOYEE_APP_URL: appUrl('EMPLOYEE_APP_URL', 'http://localhost:4200'),
    MANAGE_APP_URL: appUrl('MANAGE_APP_URL', 'http://localhost:4004'),
    INSIGHTS_APP_URL: appUrl('INSIGHTS_APP_URL', 'http://localhost:4211'),
    SETTINGS_APP_URL: appUrl('SETTINGS_APP_URL', 'http://localhost:4600'),
    MAPBOX_KEY: plain('MAPBOX_KEY'),
    CDN_BASE_URL: plain('CDN_BASE_URL'),
    TEAMS_CLIENT_ID: plain('TEAMS_CLIENT_ID'),
    TEAMS_AUTHORITY: plain('TEAMS_AUTHORITY'),
    FIREBASE_API_KEY: plain('FIREBASE_API_KEY'),
    FIREBASE_AUTH_DOMAIN: plain('FIREBASE_AUTH_DOMAIN'),
    FIREBASE_PROJECT_ID: plain('FIREBASE_PROJECT_ID'),
    FIREBASE_STORAGE_BUCKET: plain('FIREBASE_STORAGE_BUCKET'),
    FIREBASE_MESSAGING_SENDER_ID: plain('FIREBASE_MESSAGING_SENDER_ID'),
    FIREBASE_APP_ID: plain('FIREBASE_APP_ID'),
    FIREBASE_VAPID_KEY: plain('FIREBASE_VAPID_KEY'),
  };
}

/** The resolved environment — frozen at module load. */
export const RDX_VISITORS_ENV: Readonly<RdxVisitorsEnv> = Object.freeze(readRdxVisitorsEnv());
